import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Boxes } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 5 — Promoter Stock & Display Report submission (mobile).
 * Append-only: once submitted there is no edit/delete (audit trail).
 */
const initial = {
  report_date: new Date().toISOString().slice(0, 10),
  brand: "", product_category: "", product_model: "",
  available_stock_qty: "", display_qty: "",
  display_condition: "good",
  competitor_display_remarks: "",
  required_replenishment_qty: "",
  photo_url: "", remarks: "",
};

export default function PromoterStockReport() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/promoter/me"), api.get("/products")])
      .then(([p, pr]) => { setProfile(p.data); setProducts(pr.data || []); })
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const brands = Array.from(new Set(products.map((p) => p.brand).filter(Boolean))).sort();
  const categories = Array.from(new Set(
    products.filter((p) => !form.brand || p.brand === form.brand).map((p) => p.category).filter(Boolean))).sort();
  const models = Array.from(new Set(
    products
      .filter((p) => (!form.brand || p.brand === form.brand) && (!form.product_category || p.category === form.product_category))
      .map((p) => p.model).filter(Boolean))).sort();

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Photo too large (max 4 MB)"); return; }
    const r = new FileReader();
    r.onload = () => update("photo_url", r.result);
    r.onerror = () => toast.error("Could not read photo");
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand) { toast.error("Brand is required"); return; }
    if (!form.product_model) { toast.error("Product model is required"); return; }
    if (form.available_stock_qty === "" || Number(form.available_stock_qty) < 0) { toast.error("Available stock qty is required"); return; }
    if (form.display_qty === "" || Number(form.display_qty) < 0) { toast.error("Display qty is required"); return; }
    if (!["good", "average", "poor"].includes(form.display_condition)) { toast.error("Display condition is required"); return; }
    setBusy(true);
    try {
      await api.post("/promoter/stock-reports", {
        ...form,
        available_stock_qty: Number(form.available_stock_qty),
        display_qty: Number(form.display_qty),
        required_replenishment_qty: form.required_replenishment_qty === "" ? null : Number(form.required_replenishment_qty),
      });
      toast.success("Stock report submitted");
      navigate("/promoter/stock");
    } catch (err) {
      toast.error("Could not submit", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="stock-report-form">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Stock &amp; Display</h1>
        <p className="text-xs text-slate-500 mt-0.5">
          {profile?.assigned_dealer_name ? `Counter: ${profile.assigned_dealer_name}` : "Assigned dealer will be auto-attached."}
        </p>
      </header>

      <Card><CardContent className="p-4 space-y-3">
        <F label="Report date" required>
          <Input type="date" value={form.report_date} onChange={(e) => update("report_date", e.target.value)} data-testid="stock-date" />
        </F>
        <div className="grid grid-cols-2 gap-2">
          <F label="Brand" required>
            <Select value={form.brand} onValueChange={(v) => setForm((f) => ({ ...f, brand: v, product_category: "", product_model: "" }))}>
              <SelectTrigger data-testid="stock-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Category">
            <Select value={form.product_category} onValueChange={(v) => setForm((f) => ({ ...f, product_category: v, product_model: "" }))}>
              <SelectTrigger data-testid="stock-category"><SelectValue placeholder={form.brand ? "Select category" : "Pick brand first"} /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <F label="Model" required>
          <Select value={form.product_model} onValueChange={(v) => update("product_model", v)}>
            <SelectTrigger data-testid="stock-model"><SelectValue placeholder={form.brand ? "Select model" : "Pick brand first"} /></SelectTrigger>
            <SelectContent>
              {models.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No models — type below</div>}
              {models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label="Or enter model manually">
          <Input value={form.product_model} onChange={(e) => update("product_model", e.target.value)} placeholder="Manual model entry" data-testid="stock-model-manual" />
        </F>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <div className="text-sm font-semibold text-slate-900">Stock &amp; Display</div>
        <div className="grid grid-cols-2 gap-2">
          <F label="Available Stock Qty" required>
            <Input type="number" min={0} value={form.available_stock_qty}
                   onChange={(e) => update("available_stock_qty", e.target.value)} data-testid="stock-available-qty" />
          </F>
          <F label="Display Qty" required>
            <Input type="number" min={0} value={form.display_qty}
                   onChange={(e) => update("display_qty", e.target.value)} data-testid="stock-display-qty" />
          </F>
        </div>
        <F label="Display Condition" required>
          <Select value={form.display_condition} onValueChange={(v) => update("display_condition", v)}>
            <SelectTrigger data-testid="stock-condition"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="good">Good</SelectItem>
              <SelectItem value="average">Average</SelectItem>
              <SelectItem value="poor">Poor</SelectItem>
            </SelectContent>
          </Select>
        </F>
        <F label="Required Replenishment (qty)">
          <Input type="number" min={0} value={form.required_replenishment_qty}
                 onChange={(e) => update("required_replenishment_qty", e.target.value)} data-testid="stock-replenishment" />
        </F>
        <F label="Competitor Display Remarks">
          <Textarea rows={2} value={form.competitor_display_remarks}
                    onChange={(e) => update("competitor_display_remarks", e.target.value)} data-testid="stock-competitor" />
        </F>
        <F label="Photo (optional)">
          {form.photo_url ? (
            <div className="flex items-start gap-2">
              <img src={form.photo_url} alt="display" className="w-20 h-20 rounded border object-cover" />
              <button type="button" onClick={() => update("photo_url", "")}
                      className="text-xs text-rose-600 hover:underline" data-testid="stock-photo-remove">Remove</button>
            </div>
          ) : (
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                    className="w-full" data-testid="stock-photo-btn">
              <Camera size={14} className="mr-1" /> Upload photo
            </Button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment"
                 onChange={onPhoto} className="hidden" data-testid="stock-photo-input" />
        </F>
        <F label="Remarks">
          <Textarea rows={2} value={form.remarks} onChange={(e) => update("remarks", e.target.value)} data-testid="stock-remarks" />
        </F>
      </CardContent></Card>

      <Button type="submit" disabled={busy}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="stock-submit-btn">
        <Boxes size={14} className="mr-1" /> {busy ? "Submitting..." : "Submit report"}
      </Button>
    </form>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase font-semibold text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
